import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../stores/authStore';
import useLang from '../hooks/useLang';

const fmt = (v) => new Intl.NumberFormat('sw-TZ').format(Math.round(v || 0));

export default function Distribution() {
  const { T } = useLang();
  const { id } = useParams();
  const { user } = useAuthStore();
  const isMember = user?.role === 'member';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [closed, setClosed] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/groups/${id}/distribution`).then(r => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const handleClose = async () => {
    if (!confirm('Una uhakika unataka kufunga mzunguko huu? Hatua hii haiwezi kurudishwa.')) return;
    setClosing(true);
    try {
      await api.post(`/groups/${id}/close-cycle`, { status: 'distributed' });
      setClosed(true);
      load();
    } finally {
      setClosing(false);
    }
  };

  if (loading) return <div className="text-center py-20 text-gray-500">{T.common.loading}</div>;
  if (!data) return null;

  const { group, total_income, total_expenses, total_group_transfers, total_reserve, to_distribute, member_shares, dissolve_at_end, net_balance } = data;

  if (!dissolve_at_end) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center text-gray-400">
        <span className="text-5xl mb-4">🔒</span>
        <p className="text-lg font-semibold text-gray-600">Mgawanyo Haujawezeshwa</p>
        <p className="text-sm mt-1">Kikundi hiki hakijaweka mgawanyo mwishoni mwa mzunguko.</p>
        <Link to="/" className="mt-6 text-sm text-emerald-600 hover:underline">← Rudi Nyumbani</Link>
      </div>
    );
  }
  const reserveDiff = (net_balance ?? 0) - (total_reserve ?? 0);
  const reserveOk   = reserveDiff >= 0;
  const myMemberId = data.my_member_id ? String(data.my_member_id) : null;
  const myShare = myMemberId ? member_shares.find(m => String(m.id) === myMemberId) : null;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="overflow-hidden rounded-[30px] border border-white/70 bg-[linear-gradient(135deg,rgba(15,118,110,0.96),rgba(15,23,42,0.94))] px-5 py-6 text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)] sm:px-6">
        <Link to={`/groups/${id}`} className="inline-flex rounded-full bg-white/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-50">
          ← {group?.name}
        </Link>
        <div className="mt-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold sm:text-3xl">{T.dist.title}</h1>
            <p className="mt-2 text-sm text-emerald-50/80">
              {T.dist.subtitle}
            </p>
          </div>
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[24px] bg-white/12 text-3xl shadow-inner shadow-white/10">
            📊
          </div>
        </div>
      </div>

      {group.status === 'distributed' || closed ? (
        <div className="rounded-[24px] border border-green-300 bg-green-50 p-5 font-medium text-green-800">
          ✅ {T.dist.alreadyDistributed}
        </div>
      ) : null}

      {/* Summary */}
      <div className="rounded-[26px] border border-white/75 bg-white/88 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] space-y-3">
        <h2 className="font-semibold text-gray-700 mb-4">{T.dist.summary}</h2>
        <div className="space-y-2 text-sm">
          <Row label={T.dist.totalCollected} value={fmt(data.total_collected)} color="green" />
          <Row label={T.dist.finesIncome} value={fmt(data.total_fines_income)} color="green" />
          <div className="border-t pt-2">
            <Row label={T.dist.totalIncome} value={fmt(total_income)} color="green" bold />
          </div>
          <Row label={T.dist.expenses} value={`- ${fmt(total_expenses)}`} color="red" />
          {total_group_transfers > 0 && (
            <Row label={T.dist.groupTransfersDed} value={`- ${fmt(total_group_transfers)}`} color="red" />
          )}
          {+data.reserve_months > 0 && (
            <Row
              label={T.dist.reserve.replace('{n}', data.reserve_months).replace('{m}', data.member_count)}
              value={`- ${fmt(total_reserve)}`}
              color="amber"
            />
          )}
          <div className="border-t pt-2">
            <Row label={T.dist.toDistribute} value={fmt(to_distribute)} color="blue" bold />
          </div>
        </div>
      </div>

      {/* Balance vs reserve status */}
      {net_balance !== undefined && (
        <div className={`rounded-[26px] border p-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] space-y-3 ${
          reserveOk ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
        }`}>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{T.dist.currentBalance}</span>
            <span className="font-bold text-gray-800">{fmt(net_balance)} TZS</span>
          </div>
          {+data.reserve_months > 0 && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  {T.dist.reserveNeeded.replace('{n}', data.reserve_months)}
                </span>
                <span className="font-medium text-amber-700">- {fmt(total_reserve)} TZS</span>
              </div>
              <div className={`flex items-center justify-between border-t pt-3 text-sm font-semibold ${
                reserveOk ? 'border-emerald-200' : 'border-red-200'
              }`}>
                <span className={reserveOk ? 'text-emerald-700' : 'text-red-700'}>
                  {reserveOk ? `✅ ${T.dist.reserveSurplus}` : `⚠️ ${T.dist.reserveShortfall}`}
                </span>
                <span className={reserveOk ? 'text-emerald-700' : 'text-red-700'}>
                  {reserveOk ? '+' : ''}{fmt(reserveDiff)} TZS
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* My share callout — members only */}
      {isMember && myShare && (
        <div className="overflow-hidden rounded-[28px] border border-emerald-300 bg-[linear-gradient(135deg,rgba(236,253,245,0.98),rgba(209,250,229,0.95))] p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-1">{T.dist.myShare}</p>
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-3xl font-bold text-emerald-800">{fmt(Math.max(0, myShare.share_amount))} <span className="text-lg font-normal">TZS</span></p>
              <p className="mt-1 text-sm text-emerald-600">{T.dist.contributed}: <strong>{fmt(myShare.contributed)} TZS</strong></p>
              {myShare.pending_fines > 0 && (
                <p className="text-sm text-red-500">{T.dist.pendingFines}: <strong>- {fmt(myShare.pending_fines)} TZS</strong></p>
              )}
            </div>
            <span className="text-4xl">💰</span>
          </div>
        </div>
      )}

      {/* Per-member shares */}
      <div>
        <h2 className="text-lg font-semibold text-gray-700 mb-3">
          {T.dist.perMember} ({member_shares.length})
        </h2>
        <div className="space-y-3">
          <div className="space-y-3 md:hidden">
            {member_shares.map((m) => {
              const isMe = myMemberId && String(m.id) === myMemberId;
              return (
                <div key={m.id} className={`rounded-[24px] p-4 shadow-[0_14px_35px_rgba(15,23,42,0.08)] ${
                  isMe ? 'border-2 border-emerald-400 bg-emerald-50' : 'border border-white/75 bg-white/88'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-800">{m.name}</p>
                        {isMe && <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white">{T.dist.me}</span>}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{m.phone}</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-sm font-bold ${isMe ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700'}`}>
                      {fmt(Math.max(0, m.share_amount))}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <MetaTile label={T.dist.contributedCol} value={fmt(m.contributed)} tone="default" />
                    <MetaTile
                      label={T.dist.pendingFinesCol}
                      value={m.pending_fines > 0 ? `- ${fmt(m.pending_fines)}` : '—'}
                      tone={m.pending_fines > 0 ? 'danger' : 'default'}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500 text-xs uppercase">
                  <th className="text-left px-4 py-3">{T.dist.memberCol}</th>
                  <th className="text-right px-4 py-3">{T.dist.contributedCol}</th>
                  <th className="text-right px-4 py-3">{T.dist.pendingFinesCol}</th>
                  <th className="text-right px-4 py-3 text-green-700">{T.dist.receives}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {member_shares.map(m => {
                  const isMe = myMemberId && String(m.id) === myMemberId;
                  return (
                  <tr key={m.id} className={isMe ? 'bg-emerald-50 font-semibold' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <div className="flex items-center gap-2">
                        {m.name}
                        {isMe && <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white">{T.dist.me}</span>}
                      </div>
                      <div className="text-xs text-gray-400">{m.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{fmt(m.contributed)}</td>
                    <td className="px-4 py-3 text-right text-red-500">
                      {m.pending_fines > 0 ? `- ${fmt(m.pending_fines)}` : '—'}
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${isMe ? 'text-emerald-700 text-base' : 'text-green-700'}`}>
                      {fmt(Math.max(0, m.share_amount))}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t bg-gray-50">
                  <td className="px-4 py-3 font-bold text-gray-700" colSpan={3}>{T.dist.totalDist}</td>
                  <td className="px-4 py-3 text-right font-bold text-green-700 text-base">{fmt(to_distribute)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Close cycle button — admin only */}
      {!isMember && dissolve_at_end && group.status !== 'distributed' && !closed && (
        <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-5">
          <p className="text-sm text-amber-800 mb-4">
            {T.dist.closeWarning}
          </p>
          <button onClick={handleClose} disabled={closing}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium disabled:opacity-60">
            {closing ? T.dist.closing : `🔒 ${T.dist.closeCycle}`}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, color = 'gray', bold = false }) {
  const colors = { green: 'text-green-700', red: 'text-red-600', amber: 'text-amber-600', blue: 'text-blue-700', gray: 'text-gray-700' };
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={`text-gray-600 ${bold ? 'font-semibold' : ''}`}>{label}</span>
      <span className={`${bold ? 'font-bold' : 'font-medium'} ${colors[color]} text-right`}>{value} TZS</span>
    </div>
  );
}

function MetaTile({ label, value, tone = 'default' }) {
  const tones = {
    default: 'text-slate-700',
    danger: 'text-red-600',
  };

  return (
    <div className="rounded-2xl bg-slate-50 px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className={`mt-1 text-sm font-medium ${tones[tone]}`}>{value}</p>
    </div>
  );
}
